import * as React from 'react';
import styles from './styles.module.css';
import classnames from 'classnames';
import { collectAllFields, Field, FieldValue, FieldValues, Schema } from './schema';
import { buildInitialValues, dirtyKeys, formReducer } from './utils/reducer';
import SectionCard from './SectionCard';
import { DifferingKeyDetail } from './fields/FieldWrapper';
import { Breadcrumbs, Panel } from '@ixicare/ui';
import BackLink from '~components/BackLink';
import { t } from '~i18n';
import PageHeader from './PageHeader';
import { configKeysForField, mapConfigToForm, mapFormToConfig } from './utils/configMappers';
import { canRunAction, GatingContext } from './utils/gating';
import ActionBar, { BarAction } from '~components/ActionBar/ActionBar';

export interface SettingsDiffering {
  byKey: Record<string, Record<string, FieldValue>>;
  macById: Record<string, string>;
}

interface Props {
  schema: Schema;
  // The config values to seed the form with. Single device: that device's
  // settings. Bulk: the settings common to all selected devices.
  configValues: FieldValues;
  // MAC(s) of the device(s) being configured — one (single) or many (bulk).
  deviceMacs: string[];
  isInstaller: boolean;
  gating: GatingContext;
  onSubmit: (payload: FieldValues) => Promise<unknown>;
  // When present, Bulk mode: config keys that differ across the selected devices.
  differing?: SettingsDiffering;
  deviceImeis?: string[];
}

const MAX_CHIPS = 7;

const DeviceConfigForm: React.FC<Props> = ({
  schema,
  configValues,
  deviceMacs,
  isInstaller,
  gating,
  onSubmit,
  differing,
  deviceImeis,
}) => {
  const [state, dispatch] = React.useReducer(formReducer, undefined, () => {
    const schemaSeed = isInstaller ? mapConfigToForm(configValues) : configValues;
    const initial = buildInitialValues(schema, schemaSeed);
    return { values: initial, initialValues: initial, errors: {} };
  });

  // Bulk mode is the only caller that passes `differing`. Device-level fields
  // (e.g. phone_number) are per-device and hidden there.
  const isBulk = differing !== undefined;

  const dirty = React.useMemo(() => dirtyKeys(state.values, state.initialValues), [state.values, state.initialValues]);

  const fieldById = React.useMemo(() => {
    const m = new Map<string, Field>();
    for (const f of collectAllFields(schema)) m.set(f.id, f);
    return m;
  }, [schema]);

  // Only count visible changes as dirty (sub profile preset might change props
  // that aren't represented in the form). Map them to their translated labels
  // so the header tooltip can list which fields changed.
  const dirtyItems = React.useMemo(
    () => dirty.filter((id) => fieldById.has(id)).map((id) => ({ id, label: t(fieldById.get(id)!.title) })),
    [dirty, fieldById]
  );

  // Fields whose current value diverges from the site-level config, mirroring
  // the per-field indicator in FieldWrapper.
  const siteOverrideFields = React.useMemo(
    () =>
      collectAllFields(schema).filter(
        (f) => f.site_config_value !== undefined && state.values[f.id] !== f.site_config_value
      ),
    [schema, state.values]
  );

  const siteOverrideItems = React.useMemo(
    () => siteOverrideFields.map((f) => ({ id: f.id, label: t(f.title) })),
    [siteOverrideFields]
  );

  const differingDetail = React.useMemo(() => {
    if (!differing) return undefined;
    const { byKey, macById } = differing;
    const map = new Map<string, DifferingKeyDetail[]>();
    for (const f of collectAllFields(schema)) {
      const keys = configKeysForField(f.id).filter((k) => byKey[k]);
      if (!keys.length) continue;
      map.set(
        f.id,
        keys.map((key) => ({
          key,
          values: Object.entries(byKey[key]).map(([deviceId, value]) => ({
            mac: macById[deviceId] ?? deviceId,
            value,
          })),
        }))
      );
    }
    return map;
  }, [schema, differing]);

  const differingItems = React.useMemo(
    () =>
      differingDetail
        ? [...differingDetail.keys()].map((id) => ({ id, label: t(fieldById.get(id)?.title ?? id) }))
        : [],
    [differingDetail, fieldById]
  );

  const [profile, setProfile] = React.useState<string>(schema.meta.profile?.selected ?? 'custom');

  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(Object.entries(schema.sections).map(([id, s]) => [id, Boolean(s.collapsed)]))
  );

  const toggleSection = React.useCallback((id: string) => {
    setCollapsedSections((c) => ({ ...c, [id]: !c[id] }));
  }, []);

  const [pendingScrollId, setPendingScrollId] = React.useState<string>();

  const revealField = React.useCallback(
    (id: string) => {
      const sectionRecord = Object.entries(schema.sections).find(([, s]) => s.fields.some((f) => f.id === id));
      const sId = sectionRecord?.[0];
      if (sId) setCollapsedSections((c) => (c[sId] ? { ...c, [sId]: false } : c));
      setPendingScrollId(id);
    },
    [schema]
  );

  React.useEffect(() => {
    if (!pendingScrollId) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById(pendingScrollId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollId(undefined);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingScrollId, collapsedSections]);

  const handleChange = React.useCallback((id: string, value: FieldValue) => {
    dispatch({ type: 'setValue', id, value });
  }, []);

  const handleSubmit = React.useCallback(() => {
    if (Object.keys(state.errors).length > 0) return;

    const payload: FieldValues = {};
    if (isInstaller) {
      // Synthetic fields explode into several config keys, so diff at the
      // config level (not the form level) to send only the changed params.
      const current = mapFormToConfig(state.values);
      const initial = mapFormToConfig(state.initialValues);
      for (const k of new Set([...Object.keys(current), ...Object.keys(initial)])) {
        if (current[k] !== initial[k]) payload[k] = current[k];
      }
    } else {
      for (const k of dirty) payload[k] = state.values[k];
    }

    onSubmit(payload)
      .then(() => {
        dispatch({ type: 'commit', values: state.values });
      })
      .catch(() => {
        /* notification handled in store */
      });
  }, [state.errors, state.values, state.initialValues, dirty, isInstaller, onSubmit]);

  const handleReset = React.useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  const handleDownload = React.useCallback(() => {
    const blob = new Blob([JSON.stringify(state.values, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const downloadName = deviceMacs.length === 1 ? (deviceMacs[0] ?? 'export') : 'bulk';
    a.download = `device-config-${downloadName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state.values, deviceMacs]);

  // Adapt the schema's actions into the generic ActionBar contract. Submit /
  // reset / download dispatch is keyed off the action id, as it was before.
  const hasErrors = Object.keys(state.errors).length > 0;
  const barActions: BarAction[] = schema.actions
    .filter((a) => (a.placement ?? 'actionbar') === 'actionbar' && canRunAction(a, gating))
    .map((a) => ({
      id: a.id,
      label: t(a.label_key),
      variant: a.variant,
      disabled: a.id === 'submit' && (dirty.length === 0 || hasErrors),
      confirm:
        a.id === 'reset' && a.confirm ? { title: t(a.confirm.title_key), body: t(a.confirm.body_key) } : undefined,
      onClick: () => {
        if (a.id === 'submit') handleSubmit();
        else if (a.id === 'reset') handleReset();
        else if (a.id === 'download_config') handleDownload();
      },
    }));

  const headerLabel =
    deviceMacs.length === 1
      ? `${deviceMacs[0]} || IMEI: ${deviceImeis?.[0]}`
      : t('device-config.bulkDevices', { count: deviceMacs.length });
  // Cap the chip list so a large bulk selection doesn't overflow the bar.
  const chips =
    deviceMacs.length <= MAX_CHIPS
      ? deviceMacs
      : [...deviceMacs.slice(0, MAX_CHIPS), `+${deviceMacs.length - MAX_CHIPS}`];

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('device-config.crumbs.devices'),
          },
          {
            label: headerLabel,
          },
          { label: t('device-config.crumbs.config') },
        ]}
      />
      <BackLink fallbackUrl="/device-overview" name={t('device-config.goback')} />
      <Panel>
        <PageHeader
          schema={schema}
          headerLabel={headerLabel}
          dirtyItems={dirtyItems}
          siteOverrideItems={siteOverrideItems}
          differingItems={differingItems}
          profile={profile}
          gating={gating}
          onProfileChange={setProfile}
          onExportClick={handleDownload}
          onRevealField={revealField}
          collapsedSections={collapsedSections}
          setCollapsedSections={setCollapsedSections}
        />
        {schema.layout.map((row, idx) => {
          if ('section' in row) {
            const section = schema.sections[row.section];
            if (!section) return null;
            return (
              <SectionCard
                key={`s-${idx}`}
                sectionId={row.section}
                section={section}
                values={state.values}
                initialValues={state.initialValues}
                errors={state.errors}
                gating={gating}
                collapsed={collapsedSections[row.section]}
                onToggleCollapsed={() => toggleSection(row.section)}
                onChange={handleChange}
                differingDetail={differingDetail}
                hideDeviceModelLevel={isBulk}
              />
            );
          }
          if ('split' in row) {
            return (
              <div key={`r-${idx}`} className={styles.section} style={{ padding: 0, border: 0 }}>
                <div className={classnames(styles.split)}>
                  {row.split.map((id) => {
                    const sec = schema.sections[id];
                    if (!sec) return <div key={id} />;
                    return (
                      <div key={id}>
                        <SectionCard
                          sectionId={id}
                          section={sec}
                          values={state.values}
                          initialValues={state.initialValues}
                          errors={state.errors}
                          gating={gating}
                          collapsed={collapsedSections[id]}
                          onToggleCollapsed={() => toggleSection(id)}
                          onChange={handleChange}
                          differingDetail={differingDetail}
                          hideDeviceModelLevel={isBulk}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }
          return null;
        })}
        <ActionBar
          title={t('device-config.actionbar.selected')}
          chips={chips}
          actions={barActions}
          actionStyle="button"
        />
      </Panel>
    </>
  );
};

export default DeviceConfigForm;
